import React, { useState } from "react";
import { Printer, ClipboardList } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

// Shared Production Ticket — visually identical to the Large Format ticket so operators read one layout.
// Fixed order: Order # / Name / Machine / Material / [module rows] / Total / [items] / Production time (ALWAYS last).
export default function ProductionTicket({
  orderNo, setOrderNo, orderName, setOrderName,
  machine, material, rows = [], total, totalLabel = "Total elements",
  items = [], prodTime = "Standard", testid = "production-ticket",
}) {
  const [created] = useState(() => new Date().toLocaleString());
  const cleanRows = rows.filter((r) => r && r.value != null && r.value !== "");

  const print = () => {
    const rowHtml = cleanRows.map((r) => `<span class="k">${esc(r.label)}</span><span>${esc(r.value)}</span>`).join("");
    const itemHtml = items.length
      ? `<table style="width:100%;border-collapse:collapse;margin-top:10px;font-size:14px">${items.map((it) => `<tr><td style="padding:5px 12px;font-weight:700;white-space:nowrap">${esc(it.title)}</td><td style="padding:5px 12px">${esc(it.detail || "")}</td></tr>`).join("")}</table>`
      : "";
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Production Ticket</title><style>@page{size:6in 4in;margin:0.14in}html,body{margin:0;padding:0;height:auto}body{font-family:ui-sans-serif,system-ui,Arial;color:#0f172a;width:5.72in}*{page-break-inside:avoid!important;page-break-before:avoid!important;page-break-after:avoid!important}h1{font-size:15px;margin:0 0 1px}.sub{color:#64748b;font-size:8px;margin-bottom:8px;line-height:1.25}.grid{display:grid;grid-template-columns:90px 1fr;gap:3px 12px;font-size:10px}.k{color:#64748b}.pt{margin-top:8px;padding-top:5px;border-top:2px solid #0f172a;font-weight:700;font-size:11px}table{margin-top:8px!important}table td{font-size:10px!important;padding:2px 8px!important}</style></head><body>
      <h1>Production Ticket</h1>
      <div class="sub">Order #${esc(orderNo || "—")} &middot; ${esc(orderName || "—")} &middot; Created ${esc(created)}</div>
      <div class="grid">
        <span class="k">Machine</span><span>${esc(machine || "—")}</span>
        <span class="k">Material</span><span>${esc(material || "—")}</span>
        ${rowHtml}
        ${total != null ? `<span class="k">${esc(totalLabel)}</span><span>${esc(total)}</span>` : ""}
      </div>
      ${itemHtml}
      <div class="pt">Production time &middot; ${esc(prodTime)}</div>
    </body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500"><ClipboardList size={14} />Production Ticket</div>
        <Button type="button" size="sm" variant="outline" data-testid={`${testid}-print`} onClick={print} className="h-7 text-xs rounded-lg"><Printer size={13} className="mr-1" />Print</Button>
      </div>
      <div className="text-[10px] text-slate-400 -mt-2 mb-3">Created: {created}</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div><Label className="text-[10px] font-mono uppercase text-slate-400">Order #</Label><Input data-testid={`${testid}-order-no`} value={orderNo || ""} onChange={(e) => setOrderNo(e.target.value)} className="h-8 rounded-lg text-sm mt-0.5" placeholder="e.g. LF-1024" /></div>
        <div><Label className="text-[10px] font-mono uppercase text-slate-400">Order Name</Label><Input data-testid={`${testid}-order-name`} value={orderName || ""} onChange={(e) => setOrderName(e.target.value)} className="h-8 rounded-lg text-sm mt-0.5" placeholder="Customer / job" /></div>
      </div>
      <div className="text-xs space-y-1.5">
        <div className="flex justify-between gap-3"><span className="text-slate-400">Machine</span><span className="font-semibold text-right">{machine || "—"}</span></div>
        <div className="flex justify-between gap-3"><span className="text-slate-400">Material</span><span className="font-semibold text-right">{material || "—"}</span></div>
        {cleanRows.map((r, i) => (
          <div key={i} className="flex justify-between gap-3"><span className="text-slate-400">{r.label}</span><span className="font-semibold text-right">{r.value}</span></div>
        ))}
        {total != null && <div className="flex justify-between gap-3"><span className="text-slate-400">{totalLabel}</span><span className="font-semibold">{total}</span></div>}
        {items.length > 0 && (
          <div className="border-t border-slate-100 pt-2 mt-1 space-y-1">
            {items.map((it, i) => (
              <div key={i} className="flex gap-2" data-testid={`${testid}-item-${i}`}>
                <span className="font-semibold text-slate-700 whitespace-nowrap num">{it.title}</span>
                {it.detail && <span className="text-slate-500">— {it.detail}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-between border-t border-slate-100 pt-2 mt-1" data-testid={`${testid}-prodtime`}>
          <span className="text-slate-400">Production time</span>
          <span className="font-semibold text-[#2495D3]">{prodTime}</span>
        </div>
      </div>
    </div>
  );
}
